import { getIn, useFormikContext } from 'formik';
import { useCallback, useMemo } from 'react';

type Result<Value> = [Value, (value: Value, shouldValidate?: boolean) => void, (value: string | undefined) => void];

interface Options {
  onlyWhenTouched: boolean;
  onlyWhenValid: boolean;
}

/**
 * Provides a stable subscription to formik field
 *
 * @param name
 * @param options
 */
export const useFormField = <Value>(name: string, options: Partial<Options> = {}): Result<Value> => {
  const { values, errors, touched, setFieldValue, setFieldError, initialValues, setFieldTouched } = useFormikContext();

  const setValue = useCallback(
    async (value: Value, shouldValidate?: boolean) => {
      await setFieldValue(name, value, shouldValidate);
      await setFieldTouched(name, true);
    },
    [name] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const setError = useCallback(
    (value: string | undefined) => {
      setFieldError(name, value);
    },
    [name] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const currentValue = getIn(values, name);
  const currentTouched = getIn(touched, name);
  const currentError = getIn(errors, name);

  const value = useMemo(() => {
    if (!currentTouched && options.onlyWhenTouched) {
      return initialValues[name];
    }
    if (currentError && options.onlyWhenValid) {
      return initialValues[name];
    }
    return currentValue;
  }, [currentTouched, options.onlyWhenTouched, options.onlyWhenValid, currentError, currentValue, initialValues, name]);

  return [value, setValue, setError];
};
